import { useState, useMemo } from 'react'
import {
  Card,
  Tabs,
  Button,
  Input,
  Space,
  Modal,
  message,
  Badge,
  Select,
  Statistic,
  Row,
  Col,
} from 'antd'
import { PlusOutlined, ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { permissionsApi } from '@/api/permissions'
import { permissionRequestsApi } from '@/api/permission-requests'
import { hospitalsApi } from '@/api/hospitals'
import { HospitalSearch } from './HospitalSearch'
import { PermissionGrid } from './PermissionGrid'
import { PermissionModal } from './PermissionModal'
import { UserPendingRequests } from './UserPendingRequests'
import type { Hospital } from '@/types/hospital'
import type { UserPermission } from '@/types/permission'
import { getApiErrorMessage } from '@/utils/error'

interface PermissionManagerProps {
  userId: string
}

export const PermissionManager = ({ userId }: PermissionManagerProps) => {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('active')
  const [searchTerm, setSearchTerm] = useState('')
  const [stateFilter, setStateFilter] = useState<string | undefined>()
  const [selectedHospital, setSelectedHospital] = useState<Hospital | null>(null)
  const [editingPermission, setEditingPermission] = useState<UserPermission | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  // Fetch user permissions
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery({
    queryKey: ['user-permissions', userId],
    queryFn: () => permissionsApi.getUserPermissions(userId),
  })

  // Fetch pending requests count for badge
  const { data: pendingRequestsData } = useQuery({
    queryKey: ['user-pending-requests', userId],
    queryFn: () =>
      permissionRequestsApi.list({
        targetUserId: userId,
        status: 'pending', // Only fetch pending requests
        page: 1,
        pageSize: 50,
      }),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })

  const pendingRequestsCount = useMemo(() => {
    const requests = pendingRequestsData?.data?.requests || []
    return requests.length // All are pending since we filter by status
  }, [pendingRequestsData])

  // Fetch all states for filtering
  const { data: statesData } = useQuery({
    queryKey: ['hospital-states'],
    queryFn: () => hospitalsApi.getStates(),
  })

  // Fetch hospital details for permissions
  const hospitalIds = useMemo(
    () => permissionsData?.data?.permissions?.map((p) => p.hospital_id) || [],
    [permissionsData]
  )

  const { data: hospitalsData } = useQuery({
    queryKey: ['hospitals-bulk', hospitalIds],
    queryFn: () => hospitalsApi.getBulkHospitals(hospitalIds),
    enabled: hospitalIds.length > 0,
  })

  // Build hospital lookup map
  const hospitalsMap = useMemo(() => {
    if (!hospitalsData?.data) return {}
    return hospitalsData.data.reduce(
      (acc, hospital) => {
        acc[hospital.id] = hospital
        return acc
      },
      {} as Record<string, Hospital>
    )
  }, [hospitalsData])

  // Filter permissions based on search and state
  const filteredPermissions = useMemo(() => {
    if (!permissionsData?.data?.permissions) return []

    return permissionsData.data?.permissions.filter((permission) => {
      const hospital = hospitalsMap[permission.hospital_id]

      // If hospital data isn't loaded yet, keep the permission
      // (it will show without hospital details until data loads)
      if (!hospital) return true

      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        if (
          !hospital.name.toLowerCase().includes(search) &&
          !hospital.city.toLowerCase().includes(search)
        ) {
          return false
        }
      }

      // State filter
      if (stateFilter && hospital.state !== stateFilter) {
        return false
      }

      return true
    })
  }, [permissionsData, hospitalsMap, searchTerm, stateFilter])

  // Calculate statistics
  const stats = useMemo(() => {
    const permissions = permissionsData?.data?.permissions || []
    return {
      total: permissions.length,
      admin: permissions.filter((p) => p.admin).length,
      sender: permissions.filter((p) => p.sender).length,
      receiver: permissions.filter((p) => p.receiver).length,
    }
  }, [permissionsData])

  // Revoke permission mutation
  const revokePermissionMutation = useMutation({
    mutationFn: (permission: UserPermission) =>
      permissionsApi.revokeFacilityPermission(userId, permission.hospital_id),
    onSuccess: () => {
      message.success('Permission revoked successfully')
      queryClient.invalidateQueries({ queryKey: ['user-permissions', userId] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to revoke permission'))
    },
  })

  const handleAddPermission = (hospital: Hospital) => {
    setSelectedHospital(hospital)
    setEditingPermission(null)
    setModalOpen(true)
  }

  const handleEditPermission = (permission: UserPermission) => {
    const hospital = hospitalsMap[permission.hospital_id]
    if (!hospital) {
      message.error('Hospital not found')
      return
    }
    setSelectedHospital(hospital)
    setEditingPermission(permission)
    setModalOpen(true)
  }

  const handleRevokePermission = (permission: UserPermission) => {
    const hospital = hospitalsMap[permission.hospital_id]
    Modal.confirm({
      title: 'Revoke Permission',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to revoke all permissions for ${hospital?.name || 'this hospital'}?`,
      okText: 'Revoke',
      okType: 'danger',
      onOk: () => {
        revokePermissionMutation.mutate(permission)
      },
    })
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedHospital(null)
    setEditingPermission(null)
  }

  // Get hospital IDs that already have permissions
  const existingHospitalIds = useMemo(
    () => permissionsData?.data?.permissions?.map((p) => p.hospital_id) || [],
    [permissionsData]
  )

  return (
    <>
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'active',
              label: (
                <Badge count={stats.total} offset={[10, 0]}>
                  Active Permissions
                </Badge>
              ),
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Statistics Row */}
                  <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col span={6}>
                      <Statistic title="Total Hospitals" value={stats.total} />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Admin Access"
                        value={stats.admin}
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Sender"
                        value={stats.sender}
                        valueStyle={{ color: '#1677ff' }}
                      />
                    </Col>
                    <Col span={6}>
                      <Statistic
                        title="Receiver"
                        value={stats.receiver}
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                  </Row>

                  {/* Search and Filters */}
                  <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical">
                    <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space wrap>
                        <Input
                          placeholder="Search by hospital name or city"
                          prefix={<SearchOutlined />}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ width: 300 }}
                          allowClear
                        />
                        <Select
                          placeholder="Filter by state"
                          value={stateFilter}
                          onChange={setStateFilter}
                          style={{ width: 150 }}
                          allowClear
                          options={
                            statesData?.data.map((state) => ({
                              label: state,
                              value: state,
                            })) || []
                          }
                        />
                      </Space>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setActiveTab('add')}
                      >
                        Add Permission
                      </Button>
                    </Space>
                  </Space>

                  {/* Permissions Grid - Scrollable */}
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <PermissionGrid
                      permissions={filteredPermissions}
                      hospitals={hospitalsMap}
                      onEdit={handleEditPermission}
                      onRevoke={handleRevokePermission}
                      loading={permissionsLoading}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'add',
              label: 'Add Permission',
              children: (
                <div>
                  <h3 style={{ marginBottom: 16 }}>Search for a Hospital</h3>
                  <p style={{ marginBottom: 16, color: '#888' }}>
                    Start typing to search for hospitals by name, city, or CMS ID
                  </p>
                  <HospitalSearch
                    onSelect={handleAddPermission}
                    excludeIds={existingHospitalIds}
                    placeholder="Search hospitals to grant permissions..."
                  />
                </div>
              ),
            },
            {
              key: 'pending',
              label: (
                <Badge count={pendingRequestsCount} offset={[10, 0]}>
                  Pending Requests
                </Badge>
              ),
              children: <UserPendingRequests userId={userId} />,
            },
          ]}
        />
      </Card>

      {/* Permission Modal */}
      {selectedHospital && (
        <PermissionModal
          open={modalOpen}
          onCancel={handleCloseModal}
          userId={userId}
          hospital={selectedHospital}
          existingPermission={editingPermission || undefined}
        />
      )}
    </>
  )
}
